import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useT } from "../context/LanguageContext";
import { cookieT } from "../translations/ui";

export default function CookieBanner() {
  const t = useT(cookieT);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setShowBanner(false);
    setShowPreferences(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
        {!showPreferences ? (
          <>
            <div className="flex justify-between items-start mb-4">
              <h3 className="heading-md text-lg text-white">Cookie Consent</h3>
              <button
                onClick={handleAcceptNecessary}
                className="text-white/40 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="body-text text-white/60 text-sm mb-6">
              {t.text}{" "}
              <a href="/privacy" className="text-white hover:underline">{t.link}</a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={handleAcceptAll} className="btn-primary text-sm px-6 py-3">
                {t.accept}
              </button>
              <button onClick={handleAcceptNecessary} className="btn-secondary text-sm px-6 py-3">
                {t.decline}
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="text-white/60 hover:text-white text-sm uppercase tracking-wider transition-colors py-3"
              >
                Manage Preferences
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between items-start mb-6">
              <h3 className="heading-md text-lg text-white">Cookie Preferences</h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-white/40 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Necessary */}
              <div className="flex items-center justify-between p-4 border border-white/10 bg-white/5">
                <div>
                  <h4 className="text-white font-semibold text-sm">Necessary Cookies</h4>
                  <p className="text-white/40 text-xs mt-1">Essential for the website to function properly.</p>
                </div>
                <div className="text-white/40 text-xs uppercase">Always Active</div>
              </div>
              
              {/* Analytics */}
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div>
                  <h4 className="text-white font-semibold text-sm">Analytics Cookies</h4>
                  <p className="text-white/40 text-xs mt-1">Help us understand how visitors use our website.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-checked:bg-white rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white peer-checked:after:bg-black after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>
              
              {/* Marketing */}
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div>
                  <h4 className="text-white font-semibold text-sm">Marketing Cookies</h4>
                  <p className="text-white/40 text-xs mt-1">Used to deliver relevant advertisements.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-checked:bg-white rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white peer-checked:after:bg-black after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleSavePreferences}
                className="btn-primary text-sm px-6 py-3"
              >
                Save Preferences
              </button>
              <button
                onClick={() => setShowPreferences(false)}
                className="btn-secondary text-sm px-6 py-3"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
